import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { AuthState } from '../../../auth/store/auth.model';
import { selectUser } from '../../../auth/store/auth.selector';
import { ProfileApiService } from '../../../pages/profile/profile-api.service';
import { ToasterService } from '../../../providers/toaster/toaster.service';

@Component({
  selector: 'app-co-curricular-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './co-curricular-details.component.html',
  styleUrl: './co-curricular-details.component.css'
})
export class CoCurricularDetailsComponent implements OnInit {
  private readonly textMinLength = 2;
  private readonly textMaxLength = 250;
  private readonly descriptionMaxLength = 1000;
  openSection: string | null = null;
  coCurricularForm: FormGroup;
  userId: number | string | null = null;
  isSaving = false;

  constructor(
    private fb: FormBuilder,
    private store: Store<AuthState>,
    private profileApi: ProfileApiService,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef
  ) {
    this.coCurricularForm = this.fb.group({
      paperPresentations: this.fb.array([this.createPaperPresentation()]),
      competitions: this.fb.array([this.createCompetition()]),
      certificates: this.fb.array([this.createCertificate()]),
      eventsOrganised: this.fb.array([this.createEventOrganised()]),
      languagesKnown: this.fb.array([this.createLanguageKnown()]),
      computerLiteracy: this.fb.array([this.createComputerLiteracy()])
    });
  }

  ngOnInit() {
    this.store.select(selectUser).pipe(take(1)).subscribe({
      next: (user) => {
        const masterUserId =
          localStorage.getItem('master_user_id') ??
          user?.id ??
          this.getUserIdFromStorage();

        if (masterUserId) {
          this.userId = masterUserId;
          this.loadCoCurricular(masterUserId);
        }
      }
    });
  }

  get paperPresentations(): FormArray {
    return this.coCurricularForm.get('paperPresentations') as FormArray;
  }

  get competitions(): FormArray {
    return this.coCurricularForm.get('competitions') as FormArray;
  }

  get certificates(): FormArray {
    return this.coCurricularForm.get('certificates') as FormArray;
  }

  get eventsOrganised(): FormArray {
    return this.coCurricularForm.get('eventsOrganised') as FormArray;
  }

  get languagesKnown(): FormArray {
    return this.coCurricularForm.get('languagesKnown') as FormArray;
  }

  get computerLiteracy(): FormArray {
    return this.coCurricularForm.get('computerLiteracy') as FormArray;
  }

  toggleSection(section: string, event: Event) {
    event.preventDefault();
    this.openSection = this.openSection === section ? null : section;
  }

  addPaperPresentation() {
    this.paperPresentations.push(this.createPaperPresentation());
  }

  removePaperPresentation(index: number) {
    if (index > 0) {
      this.paperPresentations.removeAt(index);
    }
  }

  addCompetition() {
    this.competitions.push(this.createCompetition());
  }

  removeCompetition(index: number) {
    if (index > 0) {
      this.competitions.removeAt(index);
    }
  }

  addCertificate() {
    this.certificates.push(this.createCertificate());
  }

  removeCertificate(index: number) {
    if (index > 0) {
      this.certificates.removeAt(index);
    }
  }

  addEventOrganised() {
    this.eventsOrganised.push(this.createEventOrganised());
  }

  removeEventOrganised(index: number) {
    if (index > 0) {
      this.eventsOrganised.removeAt(index);
    }
  }

  addLanguageKnown() {
    this.languagesKnown.push(this.createLanguageKnown());
  }

  removeLanguageKnown(index: number) {
    if (index > 0) {
      this.languagesKnown.removeAt(index);
    }
  }

  addComputerLiteracy() {
    this.computerLiteracy.push(this.createComputerLiteracy());
  }

  removeComputerLiteracy(index: number) {
    if (index > 0) {
      this.computerLiteracy.removeAt(index);
    }
  }

  controlLength(group: any, controlName: string): number {
    return (group.get(controlName)?.value || '').toString().length;
  }

  isMaxLengthExceeded(group: any, controlName: string): boolean {
    const control = group.get(controlName);
    return !!(control?.hasError('maxlength') && (control.dirty || control.touched));
  }

  showError(control: AbstractControl | null): boolean {
    return !!(control?.invalid && (control.dirty || control.touched));
  }

  getTextError(control: AbstractControl | null, label: string): string {
    if (!control) {
      return '';
    }

    if (control.hasError('minlength')) {
      return `${label} must be at least ${control.getError('minlength')?.requiredLength} characters`;
    }

    if (control.hasError('maxlength')) {
      return `${label} cannot exceed ${control.getError('maxlength')?.requiredLength} characters`;
    }

    return '';
  }

  saveCoCurricular() {
    if (!this.isCoCurricularFormValid()) {
      this.coCurricularForm.markAllAsTouched();
      this.toasterService.setToaster('error', 'Validation Error', this.getValidationErrorMessage());
      return;
    }

    if (!this.userId) {
      this.toasterService.setToaster('error', 'Error', 'User ID not found.');
      return;
    }

    const payload = this.buildCoCurricularPayload();
    this.isSaving = true;
    this.profileApi.createUserCoCurricular(payload).subscribe({
      next: (res: any) => {
        this.isSaving = false;
        if (res?.status === 'success' || res?.status === 200 || res?.code === 200) {
          this.toasterService.setToaster('success', 'Success', res.message ?? 'Co-curricular details saved successfully');
          const savedList = this.extractCoCurricularList(res);
          if (savedList.length) {
            this.patchCoCurricular(savedList);
          } else {
            this.loadCoCurricular(this.userId!);
          }
        } else {
          this.toasterService.setToaster('error', 'Error', res?.message ?? 'Failed to save co-curricular details');
        }
      },
      error: (err: any) => {
        this.isSaving = false;
        this.toasterService.setToaster('error', 'Failed', err?.error?.message || err?.message || 'Unable to save co-curricular details');
      }
    });
  }

  private buildCoCurricularPayload() {
    const value = this.coCurricularForm.getRawValue();
    const payload: Record<string, unknown>[] = [];

    value.paperPresentations.forEach((item: any) => {
      if (this.hasPaperPresentationData(item)) {
        payload.push(
          this.buildApiRecord(
            'paper-presentation',
            item.conference,
            item.location,
            item.description,
            item.id
          )
        );
      }
    });

    value.competitions.forEach((item: any) => {
      if (this.hasCompetitionData(item)) {
        payload.push(
          this.buildApiRecord(
            'competition',
            item.competition,
            item.achievement,
            item.description,
            item.id
          )
        );
      }
    });

    value.certificates.forEach((item: any) => {
      if (this.hasCertificateData(item)) {
        payload.push(
          this.buildApiRecord(
            'certificate',
            item.title,
            item.institute,
            item.description,
            item.id
          )
        );
      }
    });

    value.eventsOrganised.forEach((item: any) => {
      if (this.hasEventOrganisedData(item)) {
        payload.push(
          this.buildApiRecord(
            'event-organised',
            item.title,
            item.role,
            item.description,
            item.id
          )
        );
      }
    });

    value.languagesKnown.forEach((item: any) => {
      if (this.hasLanguageKnownData(item)) {
        payload.push(
          this.buildApiRecord(
            'language-known',
            item.language,
            'N/A',
            null,
            item.id
          )
        );
      }
    });

    value.computerLiteracy.forEach((item: any) => {
      if (this.hasComputerLiteracyData(item)) {
        payload.push(
          this.buildApiRecord(
            'computer-literacy',
            item.skill,
            'N/A',
            null,
            item.id
          )
        );
      }
    });

    return payload;
  }

  private buildApiRecord(
    coCurricularType: string,
    conference: string,
    location: string,
    description: string | null,
    id?: number | string | null
  ) {
    const item: Record<string, unknown> = {
      user_id: this.userId,
      co_curricular_type: coCurricularType,
      conference: (conference ?? '').toString().trim(),
      location: (location ?? '').toString().trim(),
      description: this.toPayloadDescription(description),
      is_active: true
    };

    if (id !== null && id !== undefined && id !== '') {
      item['id'] = id;
    }

    return item;
  }

  private toPayloadDescription(value: unknown): string | null {
    const text = (value ?? '').toString().trim();
    return text || null;
  }

  private toInputValue(value: unknown): string {
    if (value === null || value === undefined || value?.toString().trim().toLowerCase() === 'null') {
      return '';
    }

    if (value?.toString().trim().toUpperCase() === 'N/A') {
      return '';
    }

    return value.toString();
  }

  private hasPaperPresentationData(item: any): boolean {
    return !!(item?.id || item?.conference?.trim() || item?.location?.trim() || item?.description?.trim());
  }

  private hasCompetitionData(item: any): boolean {
    return !!(item?.id || item?.competition?.trim() || item?.achievement?.trim() || item?.description?.trim());
  }

  private hasCertificateData(item: any): boolean {
    return !!(item?.id || item?.title?.trim() || item?.institute?.trim() || item?.description?.trim());
  }

  private hasEventOrganisedData(item: any): boolean {
    return !!(item?.id || item?.title?.trim() || item?.role?.trim() || item?.description?.trim());
  }

  private hasLanguageKnownData(item: any): boolean {
    return !!(item?.id || item?.language?.trim());
  }

  private hasComputerLiteracyData(item: any): boolean {
    return !!(item?.id || item?.skill?.trim());
  }

  private isCoCurricularFormValid(): boolean {
    const arrays = [
      { array: this.paperPresentations, hasData: (v: any) => this.hasPaperPresentationData(v) },
      { array: this.competitions, hasData: (v: any) => this.hasCompetitionData(v) },
      { array: this.certificates, hasData: (v: any) => this.hasCertificateData(v) },
      { array: this.eventsOrganised, hasData: (v: any) => this.hasEventOrganisedData(v) },
      { array: this.languagesKnown, hasData: (v: any) => this.hasLanguageKnownData(v) },
      { array: this.computerLiteracy, hasData: (v: any) => this.hasComputerLiteracyData(v) }
    ];

    return !arrays.some(({ array, hasData }) =>
      array.controls.some((control) => hasData(control.getRawValue()) && control.invalid)
    );
  }

  private getValidationErrorMessage(): string {
    const fields = [
      { array: this.paperPresentations, hasData: (v: any) => this.hasPaperPresentationData(v), label: 'Conference', controlName: 'conference' },
      { array: this.paperPresentations, hasData: (v: any) => this.hasPaperPresentationData(v), label: 'Location', controlName: 'location' },
      { array: this.paperPresentations, hasData: (v: any) => this.hasPaperPresentationData(v), label: 'Description', controlName: 'description' },
      { array: this.competitions, hasData: (v: any) => this.hasCompetitionData(v), label: 'Competition', controlName: 'competition' },
      { array: this.competitions, hasData: (v: any) => this.hasCompetitionData(v), label: 'Achievement', controlName: 'achievement' },
      { array: this.competitions, hasData: (v: any) => this.hasCompetitionData(v), label: 'Description', controlName: 'description' },
      { array: this.certificates, hasData: (v: any) => this.hasCertificateData(v), label: 'Certificate Title', controlName: 'title' },
      { array: this.certificates, hasData: (v: any) => this.hasCertificateData(v), label: 'Institute', controlName: 'institute' },
      { array: this.certificates, hasData: (v: any) => this.hasCertificateData(v), label: 'Description', controlName: 'description' },
      { array: this.eventsOrganised, hasData: (v: any) => this.hasEventOrganisedData(v), label: 'Event Title', controlName: 'title' },
      { array: this.eventsOrganised, hasData: (v: any) => this.hasEventOrganisedData(v), label: 'Role', controlName: 'role' },
      { array: this.eventsOrganised, hasData: (v: any) => this.hasEventOrganisedData(v), label: 'Description', controlName: 'description' },
      { array: this.languagesKnown, hasData: (v: any) => this.hasLanguageKnownData(v), label: 'Language Name', controlName: 'language' },
      { array: this.computerLiteracy, hasData: (v: any) => this.hasComputerLiteracyData(v), label: 'Skill', controlName: 'skill' }
    ];

    for (const field of fields) {
      const row = field.array.controls.find((control) => {
        const value = control.getRawValue();
        return field.hasData(value) && control.get(field.controlName)?.invalid;
      });
      const control = row?.get(field.controlName);
      if (control) {
        return this.getTextError(control, field.label);
      }
    }

    return 'Please correct the highlighted co-curricular fields.';
  }

  private loadCoCurricular(userId: number | string) {
    this.profileApi.getCoCurricularByUserId(userId).subscribe({
      next: (res: any) => {
        this.patchCoCurricular(this.extractCoCurricularList(res));
      },
      error: () => {
        this.toasterService.setToaster('error', 'Error', 'Unable to load co-curricular details');
      }
    });
  }

  private extractCoCurricularList(res: any): any[] {
    const candidates = [
      res?.data,
      res?.data?.co_curricular,
      res?.data?.co_curriculers,
      res?.co_curricular,
      res
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private patchCoCurricular(items: any[]) {
    const getByType = (type: string) =>
      items.filter((item) => this.normalizeType(item?.co_curricular_type) === type);

    this.replaceArray(
      this.paperPresentations,
      getByType('paper-presentation'),
      (item) => this.createPaperPresentation(this.mapApiToPaperPresentation(item))
    );
    this.replaceArray(
      this.competitions,
      getByType('competition'),
      (item) => this.createCompetition(this.mapApiToCompetition(item))
    );
    this.replaceArray(
      this.certificates,
      getByType('certificate'),
      (item) => this.createCertificate(this.mapApiToCertificate(item))
    );
    this.replaceArray(
      this.eventsOrganised,
      getByType('event-organised'),
      (item) => this.createEventOrganised(this.mapApiToEventOrganised(item))
    );
    this.replaceArray(
      this.languagesKnown,
      getByType('language-known'),
      (item) => this.createLanguageKnown(this.mapApiToLanguageKnown(item))
    );
    this.replaceArray(
      this.computerLiteracy,
      getByType('computer-literacy'),
      (item) => this.createComputerLiteracy(this.mapApiToComputerLiteracy(item))
    );

    this.cdr.detectChanges();
  }

  private normalizeType(value: unknown): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private mapApiToPaperPresentation(item: any) {
    return {
      id: item?.id ?? null,
      conference: this.toInputValue(item?.conference),
      location: this.toInputValue(item?.location),
      description: this.toInputValue(item?.description)
    };
  }

  private mapApiToCompetition(item: any) {
    return {
      id: item?.id ?? null,
      competition: this.toInputValue(item?.conference),
      achievement: this.toInputValue(item?.location),
      description: this.toInputValue(item?.description)
    };
  }

  private mapApiToCertificate(item: any) {
    return {
      id: item?.id ?? null,
      title: this.toInputValue(item?.conference),
      institute: this.toInputValue(item?.location),
      description: this.toInputValue(item?.description)
    };
  }

  private mapApiToEventOrganised(item: any) {
    return {
      id: item?.id ?? null,
      title: this.toInputValue(item?.conference),
      role: this.toInputValue(item?.location),
      description: this.toInputValue(item?.description)
    };
  }

  private mapApiToLanguageKnown(item: any) {
    return {
      id: item?.id ?? null,
      language: this.toInputValue(item?.conference)
    };
  }

  private mapApiToComputerLiteracy(item: any) {
    return {
      id: item?.id ?? null,
      skill: this.toInputValue(item?.conference)
    };
  }

  private replaceArray(formArray: FormArray, items: any[], createForm: (item: any) => FormGroup) {
    formArray.clear();

    if (!items.length) {
      if (formArray === this.paperPresentations) {
        formArray.push(this.createPaperPresentation());
      } else if (formArray === this.competitions) {
        formArray.push(this.createCompetition());
      } else if (formArray === this.certificates) {
        formArray.push(this.createCertificate());
      } else if (formArray === this.eventsOrganised) {
        formArray.push(this.createEventOrganised());
      } else if (formArray === this.languagesKnown) {
        formArray.push(this.createLanguageKnown());
      } else if (formArray === this.computerLiteracy) {
        formArray.push(this.createComputerLiteracy());
      }
      return;
    }

    items.forEach((item) => formArray.push(createForm(item)));
  }

  private createPaperPresentation(value?: any): FormGroup {
    return this.fb.group({
      id: [value?.id ?? null],
      conference: [value?.conference ?? '', this.textValidators],
      location: [value?.location ?? '', this.textValidators],
      description: [value?.description ?? '', [Validators.maxLength(this.descriptionMaxLength)]]
    });
  }

  private createCompetition(value?: any): FormGroup {
    return this.fb.group({
      id: [value?.id ?? null],
      competition: [value?.competition ?? '', this.textValidators],
      achievement: [value?.achievement ?? '', this.textValidators],
      description: [value?.description ?? '', [Validators.maxLength(this.descriptionMaxLength)]]
    });
  }

  private createCertificate(value?: any): FormGroup {
    return this.fb.group({
      id: [value?.id ?? null],
      title: [value?.title ?? '', this.textValidators],
      institute: [value?.institute ?? '', this.textValidators],
      description: [value?.description ?? '', [Validators.maxLength(this.descriptionMaxLength)]]
    });
  }

  private createEventOrganised(value?: any): FormGroup {
    return this.fb.group({
      id: [value?.id ?? null],
      title: [value?.title ?? '', this.textValidators],
      role: [value?.role ?? '', this.textValidators],
      description: [value?.description ?? '', [Validators.maxLength(this.descriptionMaxLength)]]
    });
  }

  private createLanguageKnown(value?: any): FormGroup {
    return this.fb.group({
      id: [value?.id ?? null],
      language: [value?.language ?? '', this.textValidators]
    });
  }

  private createComputerLiteracy(value?: any): FormGroup {
    return this.fb.group({
      id: [value?.id ?? null],
      skill: [value?.skill ?? '', this.textValidators]
    });
  }

  private get textValidators() {
    return [Validators.minLength(this.textMinLength), Validators.maxLength(this.textMaxLength)];
  }

  private getUserIdFromStorage(): number | null {
    const authData = localStorage.getItem('auth_data');
    if (!authData) {
      return null;
    }
    try {
      const parsed = JSON.parse(authData);
      return parsed?.user?.id ?? null;
    } catch {
      return null;
    }
  }
}
